import {
    AddToDataLayer,
    MovioTracker,
    getAttributesFromQuery,
    getAllQueryParameters,
    getQueryParameterByName,
} from '../utils'
import {
    CompositeFilterComponent,
    CompositeFilterProps,
    DateShowtimePickerComponent,
    FilmContextComponent,
    FilmDetailsComponent,
    FilmDetailsProps,
} from '@vista/omnichannel-components-ui'
import {
    // EventCarouselComponent,
    EventPromotionComponent,
    ModalSitePickerComponent,
    // RelatedFilmsCarouselComponent,
    filmDetailsButtons,
} from '../components'
import {
    FilmDetailsPageProps,
    ModalSitePickerEvents,
    ModalSitePickerPropsType,
    defaultModalSitePickerDisplayText,
    DateShowtimePickerPropsType,
} from '../types'
import {
    FilterType,
    ShowtimeFilterKey,
    getAllFilms,
    getFilmById,
    loadAllFilms,
    getFilterByIdAndType,
    filterDomainStore,
    loadShowtimesForBusinessDate,
    getSelectedSites,
    getAllSites,
    getShowtimesQuery,
    getScreeningDatesForQuery,
    loadAllSites,
    loadScreeningDatesByQuery,
    browsingDomainStore,
    hasMaximumSelectedSites,
    isMemberSignedIn,
} from '@vista/omnichannel-components-domain'
import { OpenLoginModal, addOnClickHandler, getSitesFilmIsPlaying, loadMember } from '../utils'
import { useEffect, useMemo, useRef, useState } from 'react'

import React from 'react'
import { compositeFilterProps } from '../components/filmList/props'
import { dateShowtimePickerProps } from '../components/dateShowtimePicker/props'
import { defaultFilmDetailsProps } from '@vista/omnichannel-components-ui/lib/src/lib/components/film/details/types/props'
import { runInAction } from 'mobx'
import { autorun } from 'mobx'
import RelatedFilmsCarouselComponent from '../components/carousel/carouselComponents/relatedFilms/RelatedFilmsCarouselComponent'
import EventCarouselComponent from '../components/carousel/carouselComponents/events/eventCarouselComponent'
import { observer } from 'mobx-react'

type DateShowtimePickerWrapperPropsType = {
    DateShowtimePicker: DateShowtimePickerPropsType
    filmId: string
    playingSites: string[]
    filterId: string
}

const DateShowtimePickerWrapper = observer((props: DateShowtimePickerWrapperPropsType) => {
    const getOccProps = () => {
        const initialShowtimePickerProps = { ...dateShowtimePickerProps(props.DateShowtimePicker) }
        return {
            ...initialShowtimePickerProps,
            filmIds: [props.filmId],
            siteIds: getSelectedSites()
                .filter(selectedSite => props.playingSites.includes(selectedSite.id))
                .map(selectedSite => selectedSite.id),
            config: {
                ...initialShowtimePickerProps.config,
                filter: {
                    filterId: props.filterId,
                },
            },
        }
    }
    const [occProps, setOccProps] = useState<DateShowtimePickerPropsType>(getOccProps())

    useEffect(() => {
        setOccProps(getOccProps())
        // props.playingSites.length in dependency so updates after playing sites have been loaded
    }, [browsingDomainStore.data.selectedSiteIds.length, props.playingSites.length])

    return <DateShowtimePickerComponent {...occProps} />
})

export const FilmDetailsPage: React.FC<FilmDetailsPageProps> = props => {
    const [playingSites, setPlayingSites] = useState<string[]>([])
    const {
        FilmDetails,
        SitePickerForDateShowtimePicker,
        FilmCarousel,
        WatchAtHomeComponent,
        FilmDetailsEventCarousel,
        EventPromotion,
        SuppressShowtimePickerComponent,
    } = props
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const showtimePickerRef = useRef(null)
    const [attributeIds, setAttributeIds] = useState<string[]>()
    const [isSitesChanged, setIsSitesChanged] = useState(false)
    const [isSitesChangedLocked, setIsSitesChangedLocked] = useState(false)
    const [hasViewItemFired, setHasViewItemFired] = useState(false)

    const movioTracker = new MovioTracker()

    const filmId = FilmDetails?.workflow?.entityIds?.filmId

    const description = EventPromotion?.displayText?.description
    const title = EventPromotion?.displayText?.title
    const image = EventPromotion?.displayText?.image?.desktop
    const links = EventPromotion?.displayText?.links[0]

    const eventPromoNotEmpty = title || description || image || links
    const suppressShowtimePicker = SuppressShowtimePickerComponent?.config?.suppressShowtimePicker
    const filterId = 'filter-details-' + filmId

    movioTracker.movieDetail(filmId, 30000)

    const filmDetails = getFilmById(filmId)

    useEffect(() => {
        // Hide Failed to add to watchlist notification if not logged in
        const targetNode = document.getElementById('main-notification-list')
        const config = { attributes: true, childList: true, subtree: true }
        const callback = () => {
            if (isMemberSignedIn()) return
            const notificationTitleElement: HTMLElement | null = targetNode.querySelector('.v-notification__title')
            if (notificationTitleElement && notificationTitleElement.innerHTML === 'Failed to add to watchlist') {
                const notificationElement: HTMLElement | null = targetNode.querySelector('.v-notification')
                if (notificationTitleElement) notificationElement.style.display = 'none'
            }
        }
        const observer = new MutationObserver(callback)
        observer.observe(targetNode, config)
        return () => {
            observer.disconnect()
        }
    }, [])

    useEffect(() => {
        ;(async () => {
            addOnClickHandler('.v-film-watchlist-button', watchlistButtonClick)
            if (!getAllFilms()) {
                await loadAllFilms()
            }

            // Update filter according to attribute ids in the query string
            const attributeIds = getAttributesFromQuery()
            setAttributeIds(attributeIds)

            setAttributesOnFilter(attributeIds)

            try {
                const filmIds = [filmId]
                const playingSiteIdsRes = await getSitesFilmIsPlaying({ filmIds, attributeIds })

                setPlayingSites(playingSiteIdsRes)
            } catch (e) {
                console.error(e)
            }
        })()
    }, [])

    //If there is a siteId in the url, add it to the selected sites if it isn't already
    useEffect(() => {
        const siteId = getQueryParameterByName('siteId')
        if (siteId && !browsingDomainStore.data.selectedSiteIds.includes(siteId)) {
            runInAction(() => {
                if (hasMaximumSelectedSites()) {
                    browsingDomainStore.data.selectedSiteIds[0] = siteId
                } else browsingDomainStore.data.selectedSiteIds.push(siteId)
            })
        }
    }, [])

    // Determine if there are any showtimes on any dates for any sites for this film
    useEffect(() => {
        ;(async () => {
            const allSiteIds: string[] = getAllSites()?.length
                ? getAllSites().map(site => site.id)
                : await loadAllSites().then(() => getAllSites().map(site => site.id))
            const showtimesQuery = getShowtimesQuery([filmId], allSiteIds)
            const screeningDates = getScreeningDatesForQuery(showtimesQuery)
                ? getScreeningDatesForQuery(showtimesQuery)
                : await loadScreeningDatesByQuery(showtimesQuery).then(() => getScreeningDatesForQuery(showtimesQuery))
            if (screeningDates.length) console.log('This film has screeningDates')
            else console.log('This film does not have screeningDates')
        })()
    }, [])

    if (filmDetails?.title && !hasViewItemFired) {
        setHasViewItemFired(true)
        AddToDataLayer({
            event: 'view_item',
            ecommerce: {
                items: [
                    {
                        item_name: filmDetails.title.text,
                        item_category: 'Film',
                    },
                ],
            },
        })
    }

    const setAttributesOnFilter = (attributeIds: string[]) => {
        const updateFilter = () => {
            // We have to add the filter options to the filter, otherwise we cannot enable them.
            // We want to enable filter options that don't exist on the current day so that we can get the message
            // that redirects the user to a day which does have that filter option.
            const filter = getFilterByIdAndType(filterId, FilterType.Showtime)
            runInAction(() => {
                const newFilter = { ...filter }

                newFilter.sections.attribute.options = [
                    ...filter.sections.attribute.options
                        .filter(option => !attributeIds.includes(option.value)) // Don't include an option twice
                        .map(o => {
                            return { selected: false, value: o.value }
                        }), // Only attributes from the url should be selected by default
                    ...attributeIds.map(attr => {
                        return { selected: true, value: attr }
                    }),
                ]

                filterDomainStore.data.showtimeFilters[filterId] = newFilter
            })
        }

        if (getFilterByIdAndType(filterId, FilterType.Showtime))
            updateFilter() // If filter already exists, fine - modify it
        else {
            const disposer = autorun(() => {
                // If filter doesn't exist, set up an autorun to detect when it does, modify it, then remove the autorun
                if (getFilterByIdAndType(filterId, FilterType.Showtime)) {
                    updateFilter()
                    disposer()
                }
            })
        }
    }

    const buttonMover = setInterval(function () {
        if (document.querySelector('.v-film-watchlist-button')) {
            filmDetailsButtons()
            clearInterval(buttonMover)
        }
    }, 100)

    const watchlistButtonClick = () => {
        loadMember().then(member => {
            if (!member) {
                OpenLoginModal.open()
            }
        })
    }

    const onOpenHandler = () => setIsOpen(true)

    const onCloseHandler = () => {
        setIsOpen(false)
        // Prevent onSitesChanged event from firing on subsequent interactions
        if (isSitesChanged) setIsSitesChangedLocked(true)
    }

    const onSitesChangedHandler = () => {
        if (!isSitesChanged) setIsSitesChanged(true)
        if (!isSitesChangedLocked) setAttributesOnFilter(attributeIds)
        if (getSelectedSites().length > 0) {
            document.querySelector('.main-nav').classList.remove('has-tooltip')
        }
    }

    const modalSitePickerEvents: ModalSitePickerEvents = {
        onClose: onCloseHandler,
        onSitesChanged: onSitesChangedHandler,
    }

    const sitePickerProps: ModalSitePickerPropsType = {
        show: isOpen,
        className: 'filtered',
        events: modalSitePickerEvents,
        displayText: {
            ...defaultModalSitePickerDisplayText,
        },
        sitePickerComponentProps: {
            displayText: {
                ...SitePickerForDateShowtimePicker.sitePickerComponentProps.displayText,
            },
            assets: {
                noSitesFoundIconName: 'calendar',
                failedToLoadDataIconName: 'calendar',
                removeSelectedSiteIconName: 'calendar',
            },
        },
        siteIds: playingSites,
    }

    const filmDetailsProps: FilmDetailsProps = {
        config: {
            thumbnail: {
                show: FilmDetails.config.thumbnail.show,
                mediaType: FilmDetails.config.thumbnail.mediaType,
                showWatchlistButton: false,
            },
            customFilmActions: {
                show: FilmDetails.config.customFilmActions.show,
            },
            synopsisLength: FilmDetails.config.synopsisLength,
            playTrailerButton: {
                ...defaultFilmDetailsProps.config.playTrailerButton,
                show: FilmDetails.config.playTrailer.show,
            },
            actionButton: { show: FilmDetails.config.actionButton.show },
            banner: { mediaType: FilmDetails.config.banner.mediaType },
        },
        events: {
            onActionButtonClicked: () => {
                showtimePickerRef.current.scrollIntoView()
            },
            onPlayTrailerButtonClicked: film => {
                movioTracker.movieTrailer(film.id)
            },
            onFilmAddedToWatchlist: film => {
                movioTracker.movieWatchList(film.id, false)
            },
            onFilmRemovedFromWatchlist: film => {
                movioTracker.movieWatchList(film.id, true)
            },
        },
        displayText: {
            ...FilmDetails.displayText,
        },
        assets: {
            playTrailerButtonIconName: 'play-circle',
            failedToLoadTrailerIconName: 'clapboard-with-cross-eyes',
        },
        cdnOptions: {
            filmThumbnailImage: { width: 200 },
            heroBannerImage: { width: 1920 },
            censorRatingIcon: { height: 60 },
        },
    }

    /*@ts-ignore*/
    delete filmDetailsProps.displayText.addToWatchlistLabel
    /*@ts-ignore*/
    delete filmDetailsProps.displayText.removeFromWatchlistLabel

    const initialCompositeFilterProps = {
        config: {
            filter: {
                filterId: filterId,
            },
        },
    }

    const compositeFilterPropsObj: CompositeFilterProps = {
        ...compositeFilterProps(initialCompositeFilterProps),
        displayText: {
            ...compositeFilterProps(initialCompositeFilterProps).displayText,
            resultCountMessage: '${count} showtime result(s) found',
        },
        filterId,
        config: {
            filterType: FilterType.Showtime,
            displayMode: compositeFilterProps(initialCompositeFilterProps).config.displayMode,
            filters: {
                [ShowtimeFilterKey.Attribute]: {
                    enabled: true,
                    index: 0,
                    isMultiselect: true,
                },
                [ShowtimeFilterKey.Event]: {
                    enabled: false,
                    index: 1,
                    isMultiselect: true,
                },
                [ShowtimeFilterKey.Genre]: {
                    enabled: false,
                    index: 2,
                    isMultiselect: true,
                },
            },
        },
    }

    const dateShowtimepickerWrapperProps = {
        filterId,
        filmId,
        DateShowtimePicker: props.DateShowtimePicker,
        playingSites,
    }

    /**
     * Dev Note for SitePicker and DateShowtimePicker.
     * For the SitePicker, we only want to show the Sites that of films playing
     * For the DateShowtimePicker, we only want to pass in SELECTED sites which have films playing
     * */
    return (
        <>
            <div className="width--normal padding--add">
                <FilmContextComponent filmId={filmId}>
                    <FilmDetailsComponent {...filmDetailsProps} />
                    {WatchAtHomeComponent.displayText && (
                        <a className="watch-on-demand__button" href={WatchAtHomeComponent.displayText.watchAtHomeUrl}>
                            <span>{WatchAtHomeComponent.displayText.watchAtHomeButtonLabel}</span>
                        </a>
                    )}
                </FilmContextComponent>
            </div>

            {eventPromoNotEmpty && <EventPromotionComponent {...EventPromotion} />}
            {!suppressShowtimePicker && (
                <div className="width--normal padding--add site-showtime-filter" ref={showtimePickerRef}>
                    <CompositeFilterComponent {...compositeFilterPropsObj} />
                    <div className="date-showtime-picker">
                        <DateShowtimePickerWrapper {...dateShowtimepickerWrapperProps} />
                        <ModalSitePickerComponent {...sitePickerProps} />
                        <div className="date-showtime-picker__sites-wrapper">
                            <div className="date-showtime-picker__sites-button" onClick={onOpenHandler}>
                                Choose Cinema
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {FilmCarousel?.displayText?.filmItems?.length > 0 && <RelatedFilmsCarouselComponent {...FilmCarousel} />}
            {FilmDetailsEventCarousel?.displayText?.eventItems?.length > 0 && (
                <EventCarouselComponent className="width--normal padding--add" {...FilmDetailsEventCarousel} />
            )}
        </>
    )
}

export default FilmDetailsPage
